import { PropsWithChildren, useContext, useEffect, useRef } from "react";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config/ConfigContext";
import { sidebarButtonsSize, useIsMobileSidebarLayout, useSidebarLayoutContext } from "@kaltura/ds-react-layouts";
import { styled } from "@mui/material";
import { headerHeight } from "@kaltura/mediaspace-shared-constants";
import { Box, useTheme } from "@kaltura/mediaspace-shared-styled";
import useConfig from "@kaltura/mediaspace-shared-data-kms-config/useConfig";

export enum HeroLayoutMode {
    // Take the whole width and height of the page based on the viewport (minHeight: 100vh minus header/sidebar offsets).
    // Leave a fixed height of `marginBottom` in the bottom part of the page if specified
    FullSize,
    // Render the contents in a rectangle with player's aspect ratio (16:9)
    AspectRatio,
    // Minimum: viewport height (minHeight: 100vh minus header offsets).
    // Grows with parent container height (height: 100%) when parent exceeds the viewport, as opposed to FullSize, which is always viewport-based.
    FullHeight,
}

export interface HeroLayoutProps {
    mode: HeroLayoutMode;

    /**
     * How much spacing should be left from the bottom side?
     * (applicable only for the full screen mode)
     *
     * The value is a string with CSS units, e.g. "50px" or "20vh".
     * Note: "%" is not supported as a unit, please use "vh" instead.
     */
    marginBottom?: string;

    allowBorderRadius?: boolean;

    /**
     * Minimum height floor for FullSize and FullHeight modes.
     * When the viewport-based minHeight calculation falls below this value
     * (e.g. at high browser zoom levels), this floor is used instead.
     *
     * The value is a string with CSS units, e.g. "300px".
     */
    minHeightFloor?: string;

    /**
     * Whether the layout should scroll into view when rendered.
     * This is useful in cases where the layout is rendered below the fold, and we want to scroll to it when it appears.
     * Default is false.
     */
    shouldScrollIntoView?: boolean;
}

const StyledOuterWrapper = styled(
    Box,
    {
        shouldForwardProp(propName) {
            return propName !== "mode";
        },
    }
)(({ mode }: { mode: HeroLayoutMode }) => [
    {
        position: "relative",
        width: "100%",
    },
    mode === HeroLayoutMode.FullSize && {
        display: "flex",
        flexDirection: "column",
    },
    mode === HeroLayoutMode.AspectRatio && {
        height: 0,
        paddingBottom: "56.25%",
    },
    mode === HeroLayoutMode.FullHeight && {
        height: "100%",
    },
]);
const StyledInnerWrapper = styled(Box)({
    position: "absolute",
    inset: 0,
});

/**
 * Renders container for a "hero" component.
 * See HeroLayoutMode items for details.
 */
export const HeroLayout = ({
    mode,
    marginBottom = "0px",
    allowBorderRadius = true,
    minHeightFloor,
    shouldScrollIntoView = false,
    children,
}: PropsWithChildren<HeroLayoutProps>) => {
    const configContext = useContext(ConfigContext);
    const headerColor = configContext.dsLayout?.headerColor;
    const topSpacing = headerColor && headerColor !== "transparent" ? headerHeight : 0;

    const { isEnabled } = useSidebarLayoutContext();
    const isMobileSidebarLayout = useIsMobileSidebarLayout();
    const bottomSpacing = isEnabled && isMobileSidebarLayout ? sidebarButtonsSize : 0;

    const containerRef = useRef<HTMLDivElement>(null); // ref to the container element, used for scrolling into view

    const { application } = useConfig();
    const scrollMarginTop = application?.stickyNavbar ? headerHeight : 0; // if there's a sticky navbar, we need to add a scroll margin to prevent the hero from being hidden behind the navbar when scrolling into view

    const withFloor = (value: string) => (minHeightFloor ? `max(${value}, ${minHeightFloor})` : value);

    const defaultStyle = {
        minHeight:
            mode === HeroLayoutMode.FullSize
                ? withFloor(`calc(100vh - ${topSpacing + bottomSpacing}px - ${marginBottom})`)
                : mode === HeroLayoutMode.FullHeight
                  ? withFloor(`calc(100vh - ${topSpacing + bottomSpacing}px)`)
                  : undefined,
    };

    const theme = useTheme();

    // inside KAF the hero container has a border radius
    const style =
        allowBorderRadius && configContext.application?.isKAF
            ? {
                  borderRadius: theme.kaltura.shape.roundness2,
                  overflow: "hidden",
              }
            : {};

    useEffect(() => {
        // if shouldScrollIntoView is false, do not scroll and do not add the resize listener
        if (!shouldScrollIntoView) {
            return;
        }

        // scroll into view when the component is mounted or when shouldScrollIntoView changes to true
        const scroll = () => {
            containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        };

        scroll();

        let resizeTimer: ReturnType<typeof setTimeout>;

        const handleResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(scroll, 150);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
            clearTimeout(resizeTimer);
        };
    }, [shouldScrollIntoView]);

    return (
        <StyledOuterWrapper
            ref={containerRef}
            className="kms-ds-hero-layout"
            mode={mode}
            style={{ ...defaultStyle, ...style, scrollMarginTop: `${scrollMarginTop}px` }}
        >
            <StyledInnerWrapper>{children}</StyledInnerWrapper>
        </StyledOuterWrapper>
    );
};
